import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Layout } from '@/components/Layout';
import { API } from '@/App';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Users, UserPlus, Search, Upload, FileSpreadsheet, Trash2, Edit,
  Calendar, MapPin, Phone, Mail, Building2, ChevronLeft, ChevronRight,
  CalendarDays, TrendingUp, Eye, FileText, Link2,
} from 'lucide-react';
import { toast } from 'sonner';

const initialForm = {
  rut: '',
  pasaporte: '',
  otro_documento: '',
  nombres: '',
  primer_apellido: '',
  segundo_apellido: '',
  fecha_nacimiento: '',
  comuna: '',
  direccion: '',
  telefono: '',
  email: '',
  sexo: '',
  observaciones: '',
  empresa: '',
  familia_id: '',
};

const PacientesPage = () => {
  const [tipo, setTipo] = useState('general'); // 'general' | 'empresa'
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const limit = 50;
  const [q, setQ] = useState('');
  const [stats, setStats] = useState({ total: 0, hoy: 0, semana: 0, mes: 0 });
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [formData, setFormData] = useState(initialForm);
  const [editingId, setEditingId] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailPaciente, setDetailPaciente] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [familiarDe, setFamiliarDe] = useState(null);
  const [linkTarget, setLinkTarget] = useState(null);
  const [linkSearch, setLinkSearch] = useState('');
  const [linkResults, setLinkResults] = useState([]);
  const [linkLoading, setLinkLoading] = useState(false);
  const fileInputRef = useRef(null);

  const fetchStats = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/pacientes/stats`, { params: { tipo }, withCredentials: true });
      setStats(r.data);
    } catch (e) {
      console.error(e);
    }
  }, [tipo]);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/pacientes`, {
        params: { tipo, q: q || undefined, page, limit },
        withCredentials: true,
      });
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch (e) {
      toast.error('Error al cargar pacientes');
    } finally {
      setLoading(false);
    }
  }, [tipo, q, page]);

  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => { fetchList(); }, [fetchList]);

  // Reset to page 1 when tipo or search changes
  useEffect(() => { setPage(1); }, [tipo, q]);

  const openCreate = () => {
    setEditingId(null);
    setFamiliarDe(null);
    setFormData(initialForm);
    setFormOpen(true);
  };

  const openAddFamiliar = async (p) => {
    try {
      const r = await axios.post(`${API}/pacientes/${p.id}/familia/asegurar`, {}, {
        params: { tipo }, withCredentials: true,
      });
      setEditingId(null);
      setFamiliarDe(p.nombre_completo);
      setFormData({
        ...initialForm,
        comuna: p.comuna || '',
        direccion: p.direccion || '',
        telefono: p.telefono || '',
        email: p.email || '',
        familia_id: r.data.familia_id,
      });
      setFormOpen(true);
    } catch (e) {
      console.error(e);
      toast.error('Error al preparar el familiar');
    }
  };

  const searchLinkCandidates = async (query) => {
    setLinkLoading(true);
    try {
      const r = await axios.get(`${API}/pacientes`, {
        params: { tipo, q: query || undefined, page: 1, limit: 10 },
        withCredentials: true,
      });
      setLinkResults((r.data.items || []).filter(x => x.id !== linkTarget?.id));
    } catch (e) {
      console.error(e);
    } finally {
      setLinkLoading(false);
    }
  };

  const handleVincular = async (familiar) => {
    try {
      await axios.post(`${API}/pacientes/familia/vincular`, {
        paciente_id: linkTarget.id,
        familiar_id: familiar.id,
        tipo_paciente: tipo,
        tipo_familiar: tipo,
      }, { withCredentials: true });
      toast.success(`${linkTarget.nombre_completo} y ${familiar.nombre_completo} vinculados como familia`);
      setLinkTarget(null);
      setLinkSearch('');
      setLinkResults([]);
      fetchList();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al vincular');
    }
  };

  const openEdit = (p) => {
    setEditingId(p.id);
    setFamiliarDe(null);
    setFormData({
      rut: p.rut || '',
      pasaporte: p.pasaporte || '',
      otro_documento: p.otro_documento || '',
      nombres: p.nombres || '',
      primer_apellido: p.primer_apellido || '',
      segundo_apellido: p.segundo_apellido || '',
      fecha_nacimiento: p.fecha_nacimiento || '',
      comuna: p.comuna || '',
      direccion: p.direccion || '',
      telefono: p.telefono || '',
      email: p.email || '',
      sexo: p.sexo || '',
      observaciones: p.observaciones || '',
      empresa: p.empresa || '',
      familia_id: p.familia_id || '',
    });
    setFormOpen(true);
  };

  const handleSave = async () => {
    if (!formData.nombres || !formData.primer_apellido) {
      toast.error('Nombres y primer apellido son obligatorios');
      return;
    }
    if (!formData.rut && !formData.pasaporte && !formData.otro_documento) {
      toast.error('Ingresa RUT, pasaporte u otro documento');
      return;
    }
    try {
      const payload = { ...formData };
      if (editingId) {
        await axios.put(`${API}/pacientes/${editingId}`, payload, {
          params: { tipo }, withCredentials: true,
        });
        toast.success('Paciente actualizado');
      } else {
        await axios.post(`${API}/pacientes`, payload, {
          params: { tipo }, withCredentials: true,
        });
        toast.success('Paciente creado');
      }
      setFormOpen(false);
      fetchList();
      fetchStats();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al guardar');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API}/pacientes/${deleteTarget.id}`, {
        params: { tipo }, withCredentials: true,
      });
      toast.success('Paciente eliminado');
      setDeleteTarget(null);
      fetchList();
      fetchStats();
    } catch (e) {
      toast.error('Error al eliminar');
    }
  };

  const openDetail = async (p) => {
    try {
      const r = await axios.get(`${API}/pacientes/${p.id}`, {
        params: { tipo }, withCredentials: true,
      });
      setDetailPaciente(r.data);
      setDetailOpen(true);
    } catch (e) {
      toast.error('Error al cargar detalle');
    }
  };

  const handleImport = async (file) => {
    if (!file) return;
    setImporting(true);
    setImportResult(null);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const r = await axios.post(`${API}/pacientes/importar-excel`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true,
        timeout: 600000,
      });
      setImportResult(r.data);
      toast.success('Importación completada');
      fetchList();
      fetchStats();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al importar');
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handlePdfFicha = async (p) => {
    try {
      const r = await axios.get(`${API}/reports/pdf/paciente/${p.id}`, {
        params: { tipo }, withCredentials: true, responseType: 'blob',
      });
      const blobUrl = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = blobUrl;
      const safe = (p.nombre_completo || 'paciente').replace(/[^A-Za-z0-9._-]+/g, '_');
      link.setAttribute('download', `ficha_${safe}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000);
      toast.success('Ficha PDF descargada');
    } catch (e) {
      console.error('PDF error:', e);
      toast.error(e.response?.data?.detail || 'Error al generar PDF');
    }
  };

  const totalPages = Math.ceil(total / limit) || 1;

  return (
    <Layout>
      <div className="space-y-6" data-testid="pacientes-page">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Pacientes</h1>
            <p className="text-sm text-slate-500">Base de datos de pacientes registrados</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setImportOpen(true)} data-testid="btn-import-pacientes">
              <Upload className="w-4 h-4 mr-2" /> Importar Excel
            </Button>
            <Button onClick={openCreate} data-testid="btn-new-paciente">
              <UserPlus className="w-4 h-4 mr-2" /> Nuevo paciente
            </Button>
          </div>
        </div>

        {/* Tabs general / empresa */}
        <Tabs value={tipo} onValueChange={setTipo}>
          <TabsList>
            <TabsTrigger value="general" data-testid="tab-general">
              <Users className="w-4 h-4 mr-2" /> Pacientes Generales
            </TabsTrigger>
            <TabsTrigger value="empresa" data-testid="tab-empresa">
              <Building2 className="w-4 h-4 mr-2" /> Pacientes Empresa
            </TabsTrigger>
          </TabsList>

          {/* Stats cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
            <StatCard icon={CalendarDays} title="Registrados hoy" value={stats.hoy} color="emerald" testid="stat-hoy" />
            <StatCard icon={Calendar} title="Esta semana" value={stats.semana} color="blue" testid="stat-semana" />
            <StatCard icon={TrendingUp} title="Este mes" value={stats.mes} color="purple" testid="stat-mes" />
            <StatCard icon={Users} title="Total" value={stats.total} color="slate" testid="stat-total" />
          </div>

          {/* Search */}
          <div className="mt-6">
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Buscar por RUT, nombre, email, teléfono..."
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="pl-9"
                data-testid="search-paciente"
              />
            </div>
          </div>

          {/* Table */}
          <TabsContent value={tipo} className="mt-4">
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b border-slate-200 text-slate-600">
                      <tr>
                        <th className="text-left px-4 py-3 font-medium">RUT / Documento</th>
                        <th className="text-left px-4 py-3 font-medium">Nombre completo</th>
                        <th className="text-left px-4 py-3 font-medium">Fecha Nac.</th>
                        <th className="text-left px-4 py-3 font-medium">Comuna</th>
                        <th className="text-left px-4 py-3 font-medium">Teléfono</th>
                        {tipo === 'empresa' && (
                          <th className="text-left px-4 py-3 font-medium">Empresa</th>
                        )}
                        <th className="text-right px-4 py-3 font-medium">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading && (
                        <tr><td colSpan={tipo === 'empresa' ? 7 : 6} className="text-center py-10 text-slate-500">Cargando...</td></tr>
                      )}
                      {!loading && items.length === 0 && (
                        <tr><td colSpan={tipo === 'empresa' ? 7 : 6} className="text-center py-10 text-slate-500">
                          {q ? 'No se encontraron resultados' : 'Sin pacientes registrados aún. Importa tu Excel o crea uno nuevo.'}
                        </td></tr>
                      )}
                      {!loading && items.map((p) => (
                        <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50/50" data-testid={`row-paciente-${p.id}`}>
                          <td className="px-4 py-3 font-mono text-xs text-slate-700">
                            {p.rut || p.pasaporte || p.otro_documento || '—'}
                          </td>
                          <td className="px-4 py-3 text-slate-900">{p.nombre_completo || '—'}</td>
                          <td className="px-4 py-3 text-slate-700">{p.fecha_nacimiento || '—'}</td>
                          <td className="px-4 py-3 text-slate-700">{p.comuna || '—'}</td>
                          <td className="px-4 py-3 text-slate-700">{p.telefono || '—'}</td>
                          {tipo === 'empresa' && (
                            <td className="px-4 py-3"><Badge variant="secondary">{p.empresa || '—'}</Badge></td>
                          )}
                          <td className="px-4 py-3">
                            <div className="flex justify-end gap-1">
                              <Button size="sm" variant="ghost" onClick={() => openDetail(p)} data-testid={`btn-view-${p.id}`}>
                                <Eye className="w-4 h-4" />
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => openAddFamiliar(p)} data-testid={`btn-familiar-${p.id}`} title="Agregar familiar (copia contacto)">
                                <UserPlus className="w-4 h-4 text-purple-600" />
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => { setLinkTarget(p); setLinkSearch(''); setLinkResults([]); }} data-testid={`btn-vincular-${p.id}`} title="Vincular familiar existente">
                                <Link2 className="w-4 h-4 text-purple-600" />
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => handlePdfFicha(p)} data-testid={`btn-pdf-${p.id}`} title="Descargar ficha PDF">
                                <FileText className="w-4 h-4" />
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => openEdit(p)} data-testid={`btn-edit-${p.id}`}>
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => setDeleteTarget(p)} data-testid={`btn-delete-${p.id}`}>
                                <Trash2 className="w-4 h-4 text-rose-500" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {/* Pagination */}
                <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 bg-slate-50/50">
                  <p className="text-xs text-slate-500">
                    {total > 0 ? `Mostrando ${(page - 1) * limit + 1}-${Math.min(page * limit, total)} de ${total.toLocaleString()}` : '0 resultados'}
                  </p>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(p => p - 1)} data-testid="btn-prev-page">
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <span className="text-sm text-slate-700 px-2 py-1">Pág. {page} / {totalPages}</span>
                    <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} data-testid="btn-next-page">
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Create / Edit Dialog */}
        <Dialog open={formOpen} onOpenChange={setFormOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingId ? 'Editar paciente' : (familiarDe ? 'Nuevo familiar' : 'Nuevo paciente')}</DialogTitle>
              <DialogDescription>
                {tipo === 'empresa' ? 'Paciente asociado a una empresa' : 'Paciente general (sin empresa)'}
              </DialogDescription>
            </DialogHeader>
            {familiarDe && (
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 text-sm text-purple-800 flex items-center gap-2" data-testid="familiar-banner">
                <Users className="w-4 h-4" />
                Familiar de <strong>{familiarDe}</strong> — datos de contacto copiados (puedes editarlos si difieren)
              </div>
            )}
            <div className="grid grid-cols-2 gap-4 py-2">
              <div>
                <Label>RUT</Label>
                <Input value={formData.rut} onChange={e => setFormData(f => ({ ...f, rut: e.target.value }))} placeholder="12.345.678-9" data-testid="input-rut" />
              </div>
              <div>
                <Label>Pasaporte</Label>
                <Input value={formData.pasaporte} onChange={e => setFormData(f => ({ ...f, pasaporte: e.target.value }))} data-testid="input-pasaporte" />
              </div>
              <div className="col-span-2">
                <Label>Otro documento</Label>
                <Input value={formData.otro_documento} onChange={e => setFormData(f => ({ ...f, otro_documento: e.target.value }))} data-testid="input-otro" />
              </div>
              <div className="col-span-2">
                <Label>Nombres *</Label>
                <Input value={formData.nombres} onChange={e => setFormData(f => ({ ...f, nombres: e.target.value }))} required data-testid="input-nombres" />
              </div>
              <div>
                <Label>Primer apellido *</Label>
                <Input value={formData.primer_apellido} onChange={e => setFormData(f => ({ ...f, primer_apellido: e.target.value }))} required data-testid="input-primer-apellido" />
              </div>
              <div>
                <Label>Segundo apellido</Label>
                <Input value={formData.segundo_apellido} onChange={e => setFormData(f => ({ ...f, segundo_apellido: e.target.value }))} data-testid="input-segundo-apellido" />
              </div>
              <div>
                <Label>Fecha de nacimiento</Label>
                <Input type="date" value={formData.fecha_nacimiento} onChange={e => setFormData(f => ({ ...f, fecha_nacimiento: e.target.value }))} data-testid="input-fecha-nac" />
              </div>
              <div>
                <Label>Sexo</Label>
                <Input value={formData.sexo} onChange={e => setFormData(f => ({ ...f, sexo: e.target.value }))} placeholder="M / F" data-testid="input-sexo" />
              </div>
              <div>
                <Label>Comuna</Label>
                <Input value={formData.comuna} onChange={e => setFormData(f => ({ ...f, comuna: e.target.value }))} data-testid="input-comuna" />
              </div>
              <div>
                <Label>Teléfono</Label>
                <Input value={formData.telefono} onChange={e => setFormData(f => ({ ...f, telefono: e.target.value }))} data-testid="input-telefono" />
              </div>
              <div className="col-span-2">
                <Label>Email</Label>
                <Input type="email" value={formData.email} onChange={e => setFormData(f => ({ ...f, email: e.target.value }))} data-testid="input-email" />
              </div>
              <div className="col-span-2">
                <Label>Dirección</Label>
                <Input value={formData.direccion} onChange={e => setFormData(f => ({ ...f, direccion: e.target.value }))} data-testid="input-direccion" />
              </div>
              {tipo === 'empresa' && (
                <div className="col-span-2">
                  <Label>Empresa</Label>
                  <Input value={formData.empresa} onChange={e => setFormData(f => ({ ...f, empresa: e.target.value }))} data-testid="input-empresa" />
                </div>
              )}
              <div className="col-span-2">
                <Label>Observaciones</Label>
                <Textarea value={formData.observaciones} onChange={e => setFormData(f => ({ ...f, observaciones: e.target.value }))} data-testid="input-observaciones" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setFormOpen(false)}>Cancelar</Button>
              <Button onClick={handleSave} data-testid="btn-save-paciente">{editingId ? 'Actualizar' : 'Crear'}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Detail Dialog */}
        <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{detailPaciente?.nombre_completo}</DialogTitle>
              <DialogDescription className="font-mono">
                {detailPaciente?.rut || detailPaciente?.pasaporte || detailPaciente?.otro_documento}
              </DialogDescription>
            </DialogHeader>
            {detailPaciente && (
              <div className="space-y-4">
                <div className="flex justify-end">
                  <Button size="sm" variant="outline" onClick={() => handlePdfFicha(detailPaciente)} data-testid="btn-detail-pdf">
                    <FileText className="w-4 h-4 mr-2" /> Descargar PDF
                  </Button>
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <Info icon={Calendar} label="Fecha nac." value={detailPaciente.fecha_nacimiento} />
                  <Info icon={MapPin} label="Comuna" value={detailPaciente.comuna} />
                  <Info icon={Phone} label="Teléfono" value={detailPaciente.telefono} />
                  <Info icon={Mail} label="Email" value={detailPaciente.email} />
                  {detailPaciente.empresa && <Info icon={Building2} label="Empresa" value={detailPaciente.empresa} />}
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-slate-700 mb-2">Historial de vacunaciones ({detailPaciente.historial_vacunaciones?.length || 0})</h3>
                  {(detailPaciente.historial_vacunaciones?.length || 0) === 0 ? (
                    <p className="text-sm text-slate-500">Sin registros de vacunación.</p>
                  ) : (
                    <div className="border border-slate-200 rounded-md max-h-64 overflow-y-auto">
                      {detailPaciente.historial_vacunaciones.map((v, i) => (
                        <div key={v.id || `vacc-detail-${i}`} className="px-3 py-2 border-b border-slate-100 last:border-0 text-sm">
                          <div className="flex justify-between">
                            <span className="font-medium">{v.vacuna || (v.vacunas && v.vacunas[0]?.vacuna)}</span>
                            <span className="text-slate-500">{v.fecha_vacunacion}</span>
                          </div>
                          <div className="text-xs text-slate-500">{v.dosis} · {v.tipo_vacunacion}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Import Dialog */}
        <Dialog open={importOpen} onOpenChange={setImportOpen}>
          <DialogContent className="max-w-xl">
            <DialogHeader>
              <DialogTitle>Importar pacientes desde Excel</DialogTitle>
              <DialogDescription>
                El sistema detectará automáticamente las hojas <strong>REG DIARIO</strong> (pacientes generales) y <strong>REG EMPRESAS</strong> (pacientes empresa). Los duplicados se fusionarán automáticamente.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              {!importResult && (
                <div className="border-2 border-dashed border-slate-200 rounded-lg p-8 text-center">
                  <FileSpreadsheet className="w-10 h-10 mx-auto text-slate-400 mb-2" />
                  <p className="text-sm text-slate-600 mb-3">Selecciona un archivo .xlsx</p>
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    ref={fileInputRef}
                    className="hidden"
                    onChange={(e) => handleImport(e.target.files?.[0])}
                  />
                  <Button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={importing}
                    data-testid="btn-select-excel"
                  >
                    {importing ? 'Procesando...' : 'Seleccionar archivo'}
                  </Button>
                </div>
              )}
              {importResult && (
                <div className="space-y-3" data-testid="import-result">
                  <div className="bg-emerald-50 border border-emerald-200 rounded-md p-4">
                    <h4 className="font-semibold text-emerald-900 mb-2">Pacientes generales</h4>
                    <div className="grid grid-cols-3 gap-2 text-sm">
                      <div><span className="text-slate-500">Importados:</span> <strong>{importResult.general?.importados || 0}</strong></div>
                      <div><span className="text-slate-500">Actualizados:</span> <strong>{importResult.general?.actualizados || 0}</strong></div>
                      <div><span className="text-slate-500">Saltados:</span> <strong>{importResult.general?.saltados || 0}</strong></div>
                    </div>
                  </div>
                  <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                    <h4 className="font-semibold text-blue-900 mb-2">Pacientes empresa</h4>
                    <div className="grid grid-cols-3 gap-2 text-sm">
                      <div><span className="text-slate-500">Importados:</span> <strong>{importResult.empresa?.importados || 0}</strong></div>
                      <div><span className="text-slate-500">Actualizados:</span> <strong>{importResult.empresa?.actualizados || 0}</strong></div>
                      <div><span className="text-slate-500">Saltados:</span> <strong>{importResult.empresa?.saltados || 0}</strong></div>
                    </div>
                  </div>
                  {(importResult.general?.errores?.length || importResult.empresa?.errores?.length) > 0 && (
                    <details className="text-xs text-slate-600">
                      <summary className="cursor-pointer">Ver errores</summary>
                      <ul className="mt-2 space-y-1 max-h-32 overflow-y-auto">
                        {[...(importResult.general?.errores || []), ...(importResult.empresa?.errores || [])].map((er, i) => (
                          <li key={`${i}-${er}`} className="text-rose-600">{er}</li>
                        ))}
                      </ul>
                    </details>
                  )}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setImportOpen(false); setImportResult(null); }}>Cerrar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Link family Dialog */}
        <Dialog open={!!linkTarget} onOpenChange={(o) => !o && setLinkTarget(null)}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Link2 className="w-5 h-5 text-purple-600" />
                Vincular familiar existente
              </DialogTitle>
              <DialogDescription>
                Busca un paciente ya registrado para vincularlo como familia de <strong>{linkTarget?.nombre_completo}</strong>.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div className="flex gap-2">
                <Input
                  placeholder="Buscar por RUT o nombre..."
                  value={linkSearch}
                  onChange={(e) => setLinkSearch(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && searchLinkCandidates(linkSearch)}
                  data-testid="input-link-search"
                />
                <Button variant="outline" onClick={() => searchLinkCandidates(linkSearch)} disabled={linkLoading} data-testid="btn-link-search">
                  <Search className="w-4 h-4" />
                </Button>
              </div>
              {linkResults.length > 0 && (
                <div className="border border-slate-200 rounded-md max-h-64 overflow-y-auto">
                  {linkResults.map((r) => (
                    <button
                      key={r.id}
                      type="button"
                      className="w-full text-left px-3 py-2 border-b border-slate-100 last:border-0 hover:bg-purple-50 text-sm"
                      onClick={() => handleVincular(r)}
                      data-testid={`link-result-${r.id}`}
                    >
                      <span className="font-medium">{r.nombre_completo}</span>
                      <span className="text-xs text-slate-500 font-mono ml-2">{r.rut || r.pasaporte || '—'}</span>
                    </button>
                  ))}
                </div>
              )}
              {linkLoading && <p className="text-sm text-slate-500">Buscando...</p>}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setLinkTarget(null)}>Cerrar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Dialog */}
        <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>¿Eliminar paciente?</DialogTitle>
              <DialogDescription>
                Esta acción eliminará a <strong>{deleteTarget?.nombre_completo}</strong>. Esta operación se puede revertir contactando al administrador.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancelar</Button>
              <Button variant="destructive" onClick={handleDelete} data-testid="btn-confirm-delete">Eliminar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

const StatCard = ({ icon: Icon, title, value, color, testid }) => {
  const colorMap = {
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    blue: 'bg-blue-50 text-blue-700 border-blue-200',
    purple: 'bg-purple-50 text-purple-700 border-purple-200',
    slate: 'bg-slate-50 text-slate-700 border-slate-200',
  };
  return (
    <Card data-testid={testid}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs text-slate-500 uppercase tracking-wide">{title}</p>
            <p className="text-2xl font-semibold mt-1 text-slate-900">{(value || 0).toLocaleString()}</p>
          </div>
          <div className={`p-2 rounded-md border ${colorMap[color]}`}>
            <Icon className="w-4 h-4" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const Info = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-2">
    <Icon className="w-4 h-4 text-slate-400 mt-0.5" />
    <div>
      <p className="text-xs text-slate-500">{label}</p>
      <p className="text-sm text-slate-900">{value || '—'}</p>
    </div>
  </div>
);

export default PacientesPage;
